import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Leaf, Trash2, Edit2, X, CheckSquare, Square, Zap, Scale, Star, RefreshCw, QrCode } from 'lucide-react';
import { checkAndSendReminders } from '../services/NotificationService';
import DatePicker from '../components/DatePicker';
import QRLabelsPage from './QRLabelsPage';

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_IMG = '/plant-placeholder.svg';
const resolveImageUrl = (url) => {
  if (!url) return FALLBACK_IMG;
  if (url.startsWith('http') || url.startsWith('/plant-placeholder')) return url;
  return `${API}${url}`;
};

const PHASE_OPTIONS = [
  { value: 'germination', label: 'Keimung' },
  { value: 'seedling', label: 'Sämling' },
  { value: 'vegetative', label: 'Vegetativ' },
  { value: 'flowering', label: 'Blüte' },
  { value: 'harvest', label: 'Ernte' },
];

const PHASE_LABELS = Object.fromEntries(PHASE_OPTIONS.map(p => [p.value, p.label]));

const CARE_OPTIONS = [
  { value: 'watering', label: 'Gießen' },
  { value: 'fertilizing', label: 'Düngen' },
  { value: 'training', label: 'Trainieren' },
  { value: 'topping', label: 'Topping' },
  { value: 'defoliation', label: 'Entblätterung' },
  { value: 'repotting', label: 'Umtopfen' },
  { value: 'other', label: 'Sonstiges' },
];

export default function PlantsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const defaultProject = searchParams.get('project') || '';

  const [plants, setPlants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [strains, setStrains] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [showBulkCare, setShowBulkCare] = useState(false);
  const [showBulkPhase, setShowBulkPhase] = useState(false);
  const [showQRLabels, setShowQRLabels] = useState(false);
  const [bulkPhase, setBulkPhase] = useState('vegetative');
  const [editPlant, setEditPlant] = useState(null);
  const [filterProject, setFilterProject] = useState(defaultProject);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [harvestPlant, setHarvestPlant] = useState(null);
  const [harvestForm, setHarvestForm] = useState({ harvest_date: '', wet_weight_g: '', dry_weight_g: '', quality: 0, notes: '' });

  const [form, setForm] = useState({ name: '', strain_id: '', project_id: defaultProject, grow_group_id: '', phase: 'seedling', notes: '', start_date: '' });
  const [bulkForm, setBulkForm] = useState({ base_name: '', count: 3, project_id: defaultProject, strain_id: '', grow_group_id: '', phase: 'seedling' });
  const [careForm, setCareForm] = useState({ care_type: 'watering', amount: '', notes: '' });
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingImagePreviews, setPendingImagePreviews] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterProject ? `${API}/api/plants?project_id=${filterProject}` : `${API}/api/plants`;
      const [pRes, prRes, sRes, gRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/api/projects`),
        axios.get(`${API}/api/strains`),
        axios.get(`${API}/api/groups`),
      ]);
      setPlants(Array.isArray(pRes.data) ? pRes.data : []);
      setProjects(Array.isArray(prRes.data) ? prRes.data : []);
      setStrains(Array.isArray(sRes.data) ? sRes.data : []);
      setGroups(Array.isArray(gRes.data) ? gRes.data : []);
    } catch {}
    setLoading(false);
  }, [filterProject]);

  useEffect(() => { loadData(); }, [loadData]);

  // Notification check on load
  useEffect(() => {
    if (plants.length > 0) checkAndSendReminders(plants);
  }, [plants]);

  const openCreate = () => {
    setEditPlant(null);
    setForm({ name: '', strain_id: '', project_id: filterProject, grow_group_id: '', phase: 'seedling', notes: '', start_date: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditPlant(p);
    setForm({ name: p.name, strain_id: p.strain_id || '', project_id: p.project_id, grow_group_id: p.grow_group_id || '', phase: p.phase || 'seedling', notes: p.notes || '', start_date: p.start_date || '' });
    setError('');
    setShowModal(true);
  };



  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name ist erforderlich'); return; }
    if (!form.project_id) { setError('Projekt ist erforderlich'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, strain_id: form.strain_id || null, grow_group_id: form.grow_group_id || null, start_date: form.start_date || null };
      if (editPlant) {
        const { data } = await axios.put(`${API}/api/plants/${editPlant.id}`, payload);
        setPlants(prev => prev.map(p => p.id === data.id ? data : p));
      } else {
        const { data } = await axios.post(`${API}/api/plants`, payload);
        setPlants(prev => [data, ...prev]);
      }
      setShowModal(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams({
        count: bulkForm.count,
        base_name: bulkForm.base_name,
        project_id: bulkForm.project_id,
        phase: bulkForm.phase,
      });
      if (bulkForm.strain_id) params.append('strain_id', bulkForm.strain_id);
      if (bulkForm.grow_group_id) params.append('grow_group_id', bulkForm.grow_group_id);
      const { data } = await axios.post(`${API}/api/plants/bulk?${params}`, {});
      setPlants(prev => [...(Array.isArray(data) ? data : [data]), ...prev]);
      setShowBulkCreate(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleBulkCare = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) return;
    setSaving(true);
    try {
      const { data: log } = await axios.post(`${API}/api/care-logs`, {
        plant_ids: selectedIds,
        care_type: careForm.care_type,
        amount: careForm.amount,
        notes: careForm.notes,
      });
      if (pendingImages.length > 0 && log.id) {
        for (const file of pendingImages) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${API}/api/care-logs/${log.id}/images`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      setPendingImages([]);
      setPendingImagePreviews([]);
      setSelectedIds([]);
      setShowBulkCare(false);
      setCareForm({ care_type: 'watering', amount: '', notes: '' });
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleBulkPhaseChange = async (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/plants/bulk-phase`, { plant_ids: selectedIds, phase: bulkPhase });
      setPlants(prev => prev.map(p => selectedIds.includes(p.id) ? { ...p, phase: bulkPhase } : p));
      setSelectedIds([]);
      setShowBulkPhase(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleImageSelect = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPendingImagePreviews(prev => [...prev, e.target.result]);
      setPendingImages(prev => [...prev, file]);
    };
    reader.readAsDataURL(file);
  };

  const removePendingImage = (index) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    setPendingImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const openHarvest = (plant) => {    setHarvestPlant(plant);
    setHarvestForm({ harvest_date: '', wet_weight_g: '', dry_weight_g: '', quality: 0, notes: '' });
    setError('');
  };

  const handleHarvest = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        harvest_date: harvestForm.harvest_date || null,
        wet_weight_g: harvestForm.wet_weight_g ? parseFloat(harvestForm.wet_weight_g) : null,
        dry_weight_g: harvestForm.dry_weight_g ? parseFloat(harvestForm.dry_weight_g) : null,
        quality: harvestForm.quality || null,
        notes: harvestForm.notes,
      };
      await axios.post(`${API}/api/plants/${harvestPlant.id}/harvest`, payload);
      setPlants(prev => prev.map(p => p.id === harvestPlant.id ? { ...p, phase: 'harvest', status: 'harvested' } : p));
      setHarvestPlant(null);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Pflanze wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/api/plants/${id}`);
      setPlants(prev => prev.filter(p => p.id !== id));
      setSelectedIds(prev => prev.filter(sid => sid !== id));
    } catch {}
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === plants.length ? [] : plants.map(p => p.id));
  };

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="plants-page">
      {/* QR Labels overlay */}
      {showQRLabels && (
        <QRLabelsPage
          plants={plants}
          projects={projects}
          filterProject={filterProject}
          onClose={() => setShowQRLabels(false)}
        />
      )}
      {showQRLabels ? null : (<>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Pflanzen</h1>
          <p className="text-emerald-100/50 text-sm mt-0.5">{plants.length} Pflanze(n)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedIds.length > 0 && (
            <>
              <button onClick={() => setShowBulkCare(true)} className="btn-secondary flex items-center gap-2 text-sm" data-testid="bulk-care-btn">
                <Zap size={14} /> Pflege ({selectedIds.length})
              </button>
              <button onClick={() => setShowBulkPhase(true)} className="btn-secondary flex items-center gap-2 text-sm" data-testid="bulk-phase-btn">
                <RefreshCw size={14} /> Phase ändern
              </button>
            </>
          )}
          <button onClick={() => setShowQRLabels(true)} className="btn-secondary flex items-center gap-2 text-sm" data-testid="qr-labels-btn">
            <QrCode size={14} /> QR Labels
          </button>
          <button onClick={() => setShowBulkCreate(true)} className="btn-secondary flex items-center gap-2 text-sm" data-testid="bulk-create-btn">
            <Plus size={14} /> Bulk anlegen
          </button>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="create-plant-btn">
            <Plus size={16} /> Pflanze
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        <select className="input-grow w-auto text-sm" value={filterProject} onChange={e => setFilterProject(e.target.value)} data-testid="filter-project-select">
          <option value="">Alle Projekte</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {plants.length > 0 && (
          <button onClick={toggleSelectAll} className="btn-secondary flex items-center gap-2 text-sm" data-testid="select-all-btn">
            {selectedIds.length === plants.length ? <CheckSquare size={14} /> : <Square size={14} />}
            Alle
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : plants.length === 0 ? (
        <div className="text-center py-16" data-testid="no-plants">
          <Leaf size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50 mb-4">Noch keine Pflanzen</p>
          <button onClick={openCreate} className="btn-primary">Pflanze anlegen</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="plants-grid">
          {plants.map(p => {
            const isSelected = selectedIds.includes(p.id);
            return (
              <div key={p.id} className={`card-grow overflow-hidden cursor-pointer ${isSelected ? 'border-emerald-500/60 shadow-[0_0_0_2px_rgba(16,185,129,0.3)]' : ''}`}
                data-testid={`plant-card-${p.id}`}>
                <div className="relative h-28 overflow-hidden" onClick={() => toggleSelect(p.id)}>
                  <img
                    src={resolveImageUrl(p.display_image)}
                    alt={p.name}
                    onError={(e) => { e.target.src = '/plant-placeholder.svg'; }}
                    className="w-full h-full"
                    style={p.display_image ? {objectFit:'cover'} : {objectFit:'contain', padding:'16px', background:'#111C15'}}
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(5,10,7,0.7) 0%, transparent 60%)' }} />
                  {isSelected && (
                    <div className="absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#10B981' }}>
                      <CheckSquare size={12} color="white" />
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2">
                    <span className={`badge badge-green phase-${p.phase}`}>{PHASE_LABELS[p.phase] || p.phase}</span>
                  </div>
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                      className="w-6 h-6 rounded flex items-center justify-center text-white/70 hover:text-white"
                      style={{ background: 'rgba(0,0,0,0.5)' }}>
                      <Edit2 size={10} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                      className="w-6 h-6 rounded flex items-center justify-center text-white/70 hover:text-red-400"
                      style={{ background: 'rgba(0,0,0,0.5)' }}>
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p
                    className="font-medium text-sm text-emerald-100 truncate cursor-pointer hover:text-emerald-400 transition-colors"
                    onClick={(e) => { e.stopPropagation(); navigate(`/plants/${p.id}`); }}
                    data-testid={`plant-detail-link-${p.id}`}
                  >
                    {p.name}
                  </p>
                  {p.last_care && (
                    <p className="text-xs text-emerald-100/40 mt-0.5">
                      Zuletzt: {new Date(p.last_care.performed_at).toLocaleDateString('de-DE')}
                    </p>
                  )}
                  {p.phase === 'harvest' && p.status !== 'harvested' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openHarvest(p); }}
                      className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium text-yellow-400 hover:bg-yellow-400/10 transition-colors"
                      style={{ border: '1px solid rgba(245,158,11,0.3)' }}
                      data-testid={`harvest-btn-${p.id}`}>
                      <Scale size={12} /> Ernte erfassen
                    </button>
                  )}
                  {p.status === 'harvested' && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-yellow-400/70">
                      <Scale size={11} /> Geerntet
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Plant Modal */}
      {showModal && (
        <div className="modal-backdrop" data-testid="plant-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">{editPlant ? 'Pflanze bearbeiten' : 'Neue Pflanze'}</h2>
              <button onClick={() => setShowModal(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Name *</label>
                <input className="input-grow" placeholder="z.B. Pflanze #1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="plant-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Projekt *</label>
                  <select className="input-grow" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} data-testid="plant-project-select">
                    <option value="">Wählen...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Phase</label>
                  <select className="input-grow" value={form.phase} onChange={e => setForm(f => ({ ...f, phase: e.target.value }))} data-testid="plant-phase-select">
                    {PHASE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Strain</label>
                  <select className="input-grow" value={form.strain_id} onChange={e => setForm(f => ({ ...f, strain_id: e.target.value }))} data-testid="plant-strain-select">
                    <option value="">Keiner</option>
                    {strains.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Gruppe</label>
                  <select className="input-grow" value={form.grow_group_id} onChange={e => setForm(f => ({ ...f, grow_group_id: e.target.value }))} data-testid="plant-group-select">
                    <option value="">Keine</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Startdatum</label>
                <DatePicker
                  value={form.start_date}
                  onChange={v => setForm(f => ({ ...f, start_date: v }))}
                  placeholder="Startdatum wählen"
                  data-testid="plant-start-date-input"
                />
              </div>
              <div className="text-xs text-emerald-100/40 italic py-1">ð¡ Bild wird automatisch vom Strain Ã¼bernommen</div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Notizen</label>
                <textarea className="input-grow" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-plant-btn">
                  {saving ? 'Speichern...' : editPlant ? 'Aktualisieren' : 'Anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkCreate && (
        <div className="modal-backdrop" data-testid="bulk-create-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">Pflanzen Bulk anlegen</h2>
              <button onClick={() => setShowBulkCreate(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleBulkCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Basisname</label>
                  <input className="input-grow" placeholder="z.B. OG Kush" value={bulkForm.base_name} onChange={e => setBulkForm(f => ({ ...f, base_name: e.target.value }))} required data-testid="bulk-base-name" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Anzahl</label>
                  <input type="number" min={1} max={50} className="input-grow" value={bulkForm.count} onChange={e => setBulkForm(f => ({ ...f, count: parseInt(e.target.value) }))} data-testid="bulk-count" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Projekt</label>
                <select className="input-grow" value={bulkForm.project_id} onChange={e => setBulkForm(f => ({ ...f, project_id: e.target.value }))} required data-testid="bulk-project-select">
                  <option value="">Wählen...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Strain</label>
                  <select className="input-grow" value={bulkForm.strain_id} onChange={e => setBulkForm(f => ({ ...f, strain_id: e.target.value }))}>
                    <option value="">Keiner</option>
                    {strains.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Phase</label>
                  <select className="input-grow" value={bulkForm.phase} onChange={e => setBulkForm(f => ({ ...f, phase: e.target.value }))}>
                    {PHASE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowBulkCreate(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="bulk-create-submit">
                  {saving ? 'Anlegen...' : `${bulkForm.count} Pflanzen anlegen`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Care Modal */}
      {showBulkCare && (
        <div className="modal-backdrop" data-testid="bulk-care-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">Bulk-Pflege ({selectedIds.length} Pflanzen)</h2>
              <button onClick={() => setShowBulkCare(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleBulkCare} className="space-y-3">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Pflegetyp</label>
                <select className="input-grow" value={careForm.care_type} onChange={e => setCareForm(f => ({ ...f, care_type: e.target.value }))} data-testid="bulk-care-type">
                  {CARE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Menge/Dosierung</label>
                <input className="input-grow" placeholder="z.B. 500ml, 2ml/L" value={careForm.amount} onChange={e => setCareForm(f => ({ ...f, amount: e.target.value }))} data-testid="bulk-care-amount" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Notizen</label>
                <textarea className="input-grow" rows={2} value={careForm.notes} onChange={e => setCareForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>Fotos (optional)</div>
                {pendingImagePreviews.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    {pendingImagePreviews.map((preview, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={preview} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }} />
                        <button
                          type="button"
                          onClick={() => removePendingImage(i)}
                          style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11 }}
                        >&#x2715;</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#4ade80' }}>
                    Foto aufnehmen
                    <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                      onChange={(e) => e.target.files[0] && handleImageSelect(e.target.files[0])} />
                  </label>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#60a5fa' }}>
                    Aus Galerie
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => e.target.files[0] && handleImageSelect(e.target.files[0])} />
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowBulkCare(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="bulk-care-submit">
                  {saving ? 'Speichern...' : 'Pflege eintragen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Harvest Modal */}
      {harvestPlant && (
        <div className="modal-backdrop" data-testid="harvest-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-semibold text-emerald-50">Ernte erfassen</h2>
                <p className="text-sm text-emerald-100/50 mt-0.5">{harvestPlant.name}</p>
              </div>
              <button onClick={() => setHarvestPlant(null)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleHarvest} className="space-y-4">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Erntedatum</label>
                <DatePicker
                  value={harvestForm.harvest_date}
                  onChange={v => setHarvestForm(f => ({ ...f, harvest_date: v }))}
                  placeholder="Erntedatum wählen"
                  data-testid="harvest-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Nassgewicht (g)</label>
                  <input type="number" step="0.1" min="0" className="input-grow" placeholder="z.B. 250"
                    value={harvestForm.wet_weight_g}
                    onChange={e => setHarvestForm(f => ({ ...f, wet_weight_g: e.target.value }))}
                    data-testid="harvest-wet-weight" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Trockengewicht (g)</label>
                  <input type="number" step="0.1" min="0" className="input-grow" placeholder="z.B. 55"
                    value={harvestForm.dry_weight_g}
                    onChange={e => setHarvestForm(f => ({ ...f, dry_weight_g: e.target.value }))}
                    data-testid="harvest-dry-weight" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Qualität</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button"
                      onClick={() => setHarvestForm(f => ({ ...f, quality: n }))}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${harvestForm.quality >= n ? 'text-yellow-400' : 'text-emerald-100/20 hover:text-yellow-400/50'}`}
                      style={{ background: harvestForm.quality >= n ? 'rgba(245,158,11,0.15)' : '#050A07', border: '1px solid #1B2A21' }}
                      data-testid={`quality-star-${n}`}>
                      <Star size={18} fill={harvestForm.quality >= n ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                  {harvestForm.quality > 0 && (
                    <button type="button" onClick={() => setHarvestForm(f => ({ ...f, quality: 0 }))}
                      className="text-xs text-emerald-100/30 hover:text-emerald-100/60 ml-1 transition-colors">
                      Zurücksetzen
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Notizen</label>
                <textarea className="input-grow" rows={3}
                  placeholder="Besonderheiten, Aroma, Wirkung..."
                  value={harvestForm.notes}
                  onChange={e => setHarvestForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="harvest-notes" />
              </div>
              {harvestForm.wet_weight_g && harvestForm.dry_weight_g && (
                <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                  <span className="text-emerald-100/50">Ausbeute: </span>
                  <span className="text-emerald-400 font-semibold">
                    {((parseFloat(harvestForm.dry_weight_g) / parseFloat(harvestForm.wet_weight_g)) * 100).toFixed(0)}%
                  </span>
                  <span className="text-emerald-100/30 text-xs ml-2">(Trockengewicht / Nassgewicht)</span>
                </div>
              )}
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setHarvestPlant(null)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-harvest-btn">
                  {saving ? 'Speichern...' : 'Ernte speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Phase Modal */}
      {showBulkPhase && (
        <div className="modal-backdrop" data-testid="bulk-phase-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-semibold text-emerald-50">Phase ändern</h2>
                <p className="text-sm text-emerald-100/50 mt-0.5">{selectedIds.length} Pflanze(n) ausgewählt</p>
              </div>
              <button onClick={() => setShowBulkPhase(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleBulkPhaseChange} className="space-y-4">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-2 uppercase tracking-wider">Neue Phase</label>
                <div className="grid grid-cols-2 gap-2">
                  {PHASE_OPTIONS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setBulkPhase(opt.value)}
                      className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors text-left ${bulkPhase === opt.value ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'text-emerald-100/50 hover:bg-[#050A07]'}`}
                      style={{ border: bulkPhase === opt.value ? undefined : '1px solid #1B2A21' }}
                      data-testid={`bulk-phase-option-${opt.value}`}>
                      <span className={`phase-${opt.value}`}>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowBulkPhase(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="bulk-phase-submit">
                  {saving ? 'Ändern...' : `${selectedIds.length} Pflanzen → ${PHASE_LABELS[bulkPhase]}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>)}
    </div>
  );
}
